import React, { useState, useEffect, useCallback } from 'react';
import { FaPaperPlane, FaSync, FaExclamationCircle, FaEnvelope, FaFileDownload, FaFilter, FaTimes, FaBan, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { sendBatchRctiEmails, getRctiEmailJobStatus, generateABAFile } from '../services/rctiService';
import { formatDate } from '../utils/dateUtils';
import { searchFranchisees } from '../services/franchiseeService';
import { searchHouseholds } from '../services/householdService';
import { debounce } from 'lodash';
import { InputField, SelectField } from './fields';
import { isDemoMode } from '../utils/demoMode';
import StatusPill from './StatusPill';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'notified', label: 'FSE Notified' },
  { value: 'awaiting_approval', label: 'Awaiting Approval' },
  { value: 'awaiting_payment', label: 'Awaiting Payment' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
];

const XERO_STATUS_OPTIONS = [
  { value: '', label: 'All Xero Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'AUTHORISED', label: 'Authorised' },
  { value: 'PAID', label: 'Paid' },
  { value: 'VOIDED', label: 'Voided' },
];

const calculateAmountDue = (rcti) => {
  if (rcti.Quantity == null || rcti.UnitAmount == null) return 'N/A';
  const amount = rcti.Quantity * rcti.UnitAmount;
  return `$${amount.toFixed(2)}`;
};

// Reusable debounced search helper (mirrors JobSchedulerPage)
const useSearch = (searchFunction, options = {}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const optionsString = JSON.stringify(options);

  const debouncedFetch = useCallback(
    debounce(async (term, fetchOptions) => {
      if (!searchFunction || term.length < 3) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      setIsSearching(true);
      try {
        const data = await searchFunction({ searchString: term, ...fetchOptions });
        const results = data.franchisees || data.clients || data.households || [];
        setSuggestions(results);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Search error:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [searchFunction]
  );

  useEffect(() => {
    const currentOptions = JSON.parse(optionsString);
    debouncedFetch(searchTerm, currentOptions);
    return () => debouncedFetch.cancel();
  }, [searchTerm, optionsString, debouncedFetch]);

  return { searchTerm, setSearchTerm, suggestions, setSuggestions, isSearching, showSuggestions, setShowSuggestions };
};

const formatHouseholdLabel = (household) => {
  if (!household) return 'N/A';
  const name = `${household.firstName || ''} ${household.lastName || ''}`.trim() || household.householdName || 'N/A';
  const address = household.addressLine1 ? ` (${household.addressLine1})` : '';
  return `${name}${address}`;
};

const RCTITabContent = ({
  rctis,
  loading,
  error,
  selectedRCTIs,
  selectedStatus,
  sendingToXero,
  isSyncing,
  isSendToXeroDisabled,
  draftCount,
  selectableDraftsCount,
  allDraftSelected,
  onStatusChange,
  onSelectRCTI,
  onSelectAllDraft,
  onSendToXero,
  onSync,
  onRowClick,
  onXeroStatusClick,
  onRefreshData, // Add this prop to refresh RCTI data after email completion
  currentPage,
  pageSize,
  hasNextPage,
  onPageChange,
  onPageSizeChange,
  isNavigating,
  filterFseCode,
  filterHouseholdId,
  filterXeroStatus,
  filterRctiNumber,
  sortDirection,
  onFilterChange,
  onClearFilters,
  onSortDirectionChange,
  onCompletionDateClick,
  onVoidRCTI,
  voidingRCTIId,
  duplicatesCount,
  onCheckDuplicates,
  onViewDuplicates,
  checkingDuplicates
}) => {
  // State for batch email sending
  const [isSendingEmails, setIsSendingEmails] = useState(false);
  const [emailJobSuccessMessage, setEmailJobSuccessMessage] = useState('');
  const [emailJobErrorMessage, setEmailJobErrorMessage] = useState('');

  // Local state for autocomplete
  const [showFilters, setShowFilters] = useState(true);
  const franchiseeSearch = useSearch(searchFranchisees);
  const householdSearch = useSearch(searchHouseholds);

  // Local state for display values (separate from search terms)
  const [fseDisplayValue, setFseDisplayValue] = useState(filterFseCode || '');
  const [householdDisplayValue, setHouseholdDisplayValue] = useState(filterHouseholdId ? `Client ${filterHouseholdId.substring(0,6)}...` : '');

  useEffect(() => {
    if (filterFseCode) {
      franchiseeSearch.setShowSuggestions(false);
    }
    if (filterHouseholdId) {
      householdSearch.setShowSuggestions(false);
    }
  }, [filterFseCode, filterHouseholdId, franchiseeSearch, householdSearch]);

  // State for email job status polling
  const [currentJobId, setCurrentJobId] = useState(null);
  const [emailJobStatus, setEmailJobStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [numberOfRctisBeingEmailed, setNumberOfRctisBeingEmailed] = useState(0);
  const [pollingMessage, setPollingMessage] = useState('');

  const handleFseInputChange = (value) => {
    setFseDisplayValue(value);
    franchiseeSearch.setSearchTerm(value);
    onFilterChange('fseCode', null);
    if (value.length < 3) {
      franchiseeSearch.setSuggestions([]);
      franchiseeSearch.setShowSuggestions(false);
    }
  };

  const handleHouseholdInputChange = (value) => {
    setHouseholdDisplayValue(value);
    householdSearch.setSearchTerm(value);
    onFilterChange('householdId', null);
    if (value.length < 3) {
      householdSearch.setSuggestions([]);
      householdSearch.setShowSuggestions(false);
    }
  };

  const handleFseSelect = (fse) => {
    const contactName = `${fse.firstName || ''} ${fse.lastName || ''}`.trim();
    const displayName = `${fse.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
    setFseDisplayValue(displayName);
    franchiseeSearch.setSuggestions([]);
    franchiseeSearch.setShowSuggestions(false);
    onFilterChange('fseCode', fse.fseCode, { autoApply: true });
  };

  const handleHouseholdSelect = (household) => {
    const displayName = formatHouseholdLabel(household);
    setHouseholdDisplayValue(displayName);
    householdSearch.setSuggestions([]);
    householdSearch.setShowSuggestions(false);
    onFilterChange('householdId', household.id, { autoApply: true });
  };

  const handleApplyFilters = () => {
    onFilterChange('apply');
  };

  const handleClearFiltersLocal = () => {
    setFseDisplayValue('');
    setHouseholdDisplayValue('');
    franchiseeSearch.setSearchTerm('');
    householdSearch.setSearchTerm('');
    franchiseeSearch.setSuggestions([]);
    householdSearch.setSuggestions([]);
    franchiseeSearch.setShowSuggestions(false);
    householdSearch.setShowSuggestions(false);
    onClearFilters();
  };

  // State for ABA file generation
  const [isGeneratingABA, setIsGeneratingABA] = useState(false);
  const [abaSuccessMessage, setAbaSuccessMessage] = useState('');
  const [abaErrorMessage, setAbaErrorMessage] = useState('');

  // Log raw data for debugging - first 2 RCTIs
  useEffect(() => {
    if (rctis && rctis.length > 0) {
      console.log('=== RCTI RAW DATA DEBUG ===');
      console.log('First RCTI:', JSON.stringify(rctis[0], null, 2));
      if (rctis.length > 1) {
        console.log('Second RCTI:', JSON.stringify(rctis[1], null, 2));
      }
      console.log('=== END RCTI RAW DATA ===');
    }
  }, [rctis]);

  const handleSendSelectedEmails = async () => {
    if (selectedRCTIs.size === 0) {
      setEmailJobErrorMessage('No RCTIs selected to email.');
      return;
    }

    setIsSendingEmails(true);
    setEmailJobSuccessMessage('');
    setEmailJobErrorMessage('');
    setPollingMessage('');

    try {
      const rctiIdsToSend = Array.from(selectedRCTIs);
      // Filter out already-sent items (already sent to Xero AND not in draft status)
      const validRctisToEmail = rctis
        .filter(rcti => {
          if (!rctiIdsToSend.includes(rcti.id)) return false;
          // Allow emailing unless both conditions are true: already sent to Xero AND status is not draft
          return !(rcti.xero_submission_status === 'success' && rcti.status !== 'draft');
        })
        .map(rcti => rcti.id);

      if (validRctisToEmail.length === 0) {
        setEmailJobErrorMessage('All selected RCTIs have already been processed and cannot be emailed again.');
        setIsSendingEmails(false);
        return;
      }

      if (validRctisToEmail.length < rctiIdsToSend.length) {
        const skippedCount = rctiIdsToSend.length - validRctisToEmail.length;
        setEmailJobErrorMessage(`${skippedCount} selected RCTI(s) were skipped because they have already been processed. Processing ${validRctisToEmail.length} remaining RCTI(s).`);
      }

      const response = await sendBatchRctiEmails({ rctiIds: validRctisToEmail });
      setEmailJobSuccessMessage(`Batch RCTI email job initiated successfully. Job ID: ${response.jobId}`);
      
      // Start polling for job status
      setCurrentJobId(response.jobId);
      setNumberOfRctisBeingEmailed(validRctisToEmail.length);
      setIsPolling(true);
      setPollingMessage(`Processing ${validRctisToEmail.length} RCTI emails...`);
      
      // Clear any previous job status
      setEmailJobStatus(null);
    } catch (error) {
      console.error("Error sending batch RCTI emails:", error);
      setEmailJobErrorMessage(error.message || 'Failed to initiate batch RCTI email job.');
    } finally {
      setIsSendingEmails(false);
    }
  };



  // Effect to poll email job status with dynamic intervals
  useEffect(() => {
    let intervalId;

    const getPollingInterval = () => {
      if (numberOfRctisBeingEmailed <= 10) return 5000; // 5 seconds for up to 10 RCTIs
      if (numberOfRctisBeingEmailed <= 30) return 10000; // 10 seconds for 10-30 RCTIs
      return 20000; // 20 seconds for more than 30 RCTIs
    };

    const fetchEmailJobStatus = async () => {
      if (!currentJobId) return;

      try {
        const jobStatusData = await getRctiEmailJobStatus(currentJobId);
        setEmailJobStatus(jobStatusData);

        // Update polling message based on job status
        if (jobStatusData.status === 'in_progress' || jobStatusData.status === 'processing') {
          const processed = jobStatusData.processed_count || 0;
          const total = jobStatusData.total_count || jobStatusData.totalToProcess || numberOfRctisBeingEmailed;
          setPollingMessage(`Processing ${processed} of ${total} emails...`);
        } else if (jobStatusData.status === 'completed') {
          const successful = jobStatusData.successfullySentCount || jobStatusData.successful_count || 0;
          const failed = jobStatusData.failedToSendCount || jobStatusData.failed_count || 0;
          setPollingMessage(`Email job completed: ${successful} sent successfully${failed > 0 ? `, ${failed} failed` : ''}.`);
          setEmailJobSuccessMessage(`Email job completed: ${successful} sent successfully${failed > 0 ? `, ${failed} failed` : ''}.`);
          setIsPolling(false);
          // Clear selected RCTIs after successful completion and refresh data
          if (successful > 0) {
            setTimeout(() => {
              // Clear selections by deselecting each currently selected RCTI
              if (selectedRCTIs.size > 0 && typeof onSelectRCTI === 'function') {
                // Deselect each currently selected RCTI
                Array.from(selectedRCTIs).forEach(rctiId => {
                  const rcti = rctis.find(r => r.id === rctiId);
                  if (rcti) {
                    onSelectRCTI(rctiId, rcti.status);
                  }
                });
              }
              // Refresh RCTI data to show updated status
              if (typeof onRefreshData === 'function') {
                onRefreshData();
              }
            }, 1000); // Small delay to show completion message
          }
        } else if (jobStatusData.status === 'completed_with_errors') {
          const successful = jobStatusData.successfullySentCount || jobStatusData.successful_count || 0;
          const failed = jobStatusData.failedToSendCount || jobStatusData.failed_count || 0;
          setPollingMessage(`Email job completed with errors: ${successful} sent successfully, ${failed} failed.`);
          setEmailJobSuccessMessage(`Email job completed with errors: ${successful} sent successfully, ${failed} failed.`);
          setIsPolling(false);
          // Clear selected RCTIs after completion with errors if some were successful and refresh data
          if (successful > 0) {
            setTimeout(() => {
              // Clear selections by deselecting each currently selected RCTI
              if (selectedRCTIs.size > 0 && typeof onSelectRCTI === 'function') {
                // Deselect each currently selected RCTI
                Array.from(selectedRCTIs).forEach(rctiId => {
                  const rcti = rctis.find(r => r.id === rctiId);
                  if (rcti) {
                    onSelectRCTI(rctiId, rcti.status);
                  }
                });
              }
              // Refresh RCTI data
              if (typeof onRefreshData === 'function') {
                onRefreshData();
              }
            }, 1000);
          }
        } else if (jobStatusData.status === 'failed') {
          setPollingMessage('Email job failed.');
          setEmailJobErrorMessage(jobStatusData.error_message || 'Email job failed.');
          setIsPolling(false);
        }

        // Stop polling when job is complete
        if (['completed', 'completed_with_errors', 'failed'].includes(jobStatusData.status)) {
          setIsPolling(false);
        }
      } catch (error) {
        console.error("Error fetching email job status:", error);
        setEmailJobErrorMessage('Failed to fetch email job status.');
        setIsPolling(false);
      }
    };

    if (isPolling && currentJobId) {
      const interval = getPollingInterval();
      intervalId = setInterval(fetchEmailJobStatus, interval);
      // Fetch immediately on start
      fetchEmailJobStatus();
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [currentJobId, isPolling, numberOfRctisBeingEmailed]);


  // Helper function to clear polling state
  const clearPollingState = () => {
    setCurrentJobId(null);
    setEmailJobStatus(null);
    setIsPolling(false);
    setPollingMessage('');
    setNumberOfRctisBeingEmailed(0);
  };

  const getStatusPillProps = (status) => {
    const normalized = status || '';
    const pillClass = normalized === 'deleted' || normalized === 'void'
      ? 'bg-red-100 text-red-800'
      : normalized === 'awaiting_payment'
      ? 'bg-orange-100 text-orange-600'
      : normalized === 'paid'
      ? 'bg-green-100 text-green-800'
      : normalized === 'draft'
      ? 'bg-yellow-100 text-yellow-800'
      : 'bg-gray-100 text-gray-800';

    const label = normalized
      ? normalized.charAt(0).toUpperCase() + normalized.slice(1).replace('_', ' ')
      : 'N/A';

    return { pillClass, label };
  };

  const renderErrorMessage = (message) => {
    if (!message) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = String(message).split(urlRegex);
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={`error-link-${index}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-red-800 break-all"
          >
            {part}
          </a>
        );
      }
      return <span key={`error-text-${index}`}>{part}</span>;
    });
  };

  // Handle ABA file generation
  const handleGenerateABA = async () => {
    setIsGeneratingABA(true);
    setAbaSuccessMessage('');
    setAbaErrorMessage('');

    try {
      // Get eligible RCTIs: paid invoices + not already in ABA file
      const eligibleRCTIs = rctis.filter(rcti =>
        rcti.relatedInvoice &&
        rcti.relatedInvoice.status === 'paid' &&
        rcti.abaIncluded !== true
      );

      let rctiIdsArray;

      if (selectedRCTIs.size > 0) {
        // Generate for selected eligible RCTIs only
        rctiIdsArray = Array.from(selectedRCTIs).filter(id =>
          eligibleRCTIs.some(rcti => rcti.id === id)
        );

        if (rctiIdsArray.length === 0) {
          setAbaErrorMessage('Selected RCTIs are not eligible (must have paid invoices and not already be in an ABA file).');
          setIsGeneratingABA(false);
          return;
        }
      } else {
        // Generate for all eligible RCTIs
        rctiIdsArray = eligibleRCTIs.map(rcti => rcti.id);

        if (rctiIdsArray.length === 0) {
          setAbaErrorMessage('No eligible RCTIs found (must have paid invoices and not already be in an ABA file).');
          setIsGeneratingABA(false);
          return;
        }
      }

      console.log(`Generating ABA file for ${rctiIdsArray.length} eligible RCTIs`);

      const result = await generateABAFile(rctiIdsArray);

      // Create and download the ABA file
      const blob = new Blob([result.content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setAbaSuccessMessage(
        `ABA file generated successfully! ${result.transactionCount} RCTI(s), Total: $${result.totalAmount.toFixed(2)}`
      );

      // Clear selections after successful ABA generation
      if (selectedRCTIs.size > 0) {
        Array.from(selectedRCTIs).forEach(rctiId => {
          const rcti = rctis.find(r => r.id === rctiId);
          if (rcti) {
            onSelectRCTI(rctiId, rcti.status);
          }
        });
      }

      // Refresh data to show updated abaIncluded status
      if (typeof onRefreshData === 'function') {
        setTimeout(() => {
          onRefreshData();
        }, 1000);
      }
    } catch (error) {
      console.error('Error generating ABA file:', error);
      setAbaErrorMessage(error.message || 'Failed to generate ABA file');
    } finally {
      setIsGeneratingABA(false);
    }
  };

  return (
    <div>
      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded break-words whitespace-normal">
          {renderErrorMessage(error)}
        </div>
      )}

      {/* ABA Generation Messages */}
      {abaSuccessMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded flex justify-between items-center">
          <span>{abaSuccessMessage}</span>
          <button 
            onClick={() => setAbaSuccessMessage('')}
            className="text-green-500 hover:text-green-700 ml-4"
          >
            ✕
          </button>
        </div>
      )}
      {abaErrorMessage && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded flex justify-between items-center">
          <span>{abaErrorMessage}</span>
          <button 
            onClick={() => setAbaErrorMessage('')}
            className="text-red-500 hover:text-red-700 ml-4"
          >
            ✕
          </button>
        </div>
      )}

      {/* Email Job Messages */}
      {emailJobSuccessMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded flex justify-between items-center">
          <span>{emailJobSuccessMessage}</span>
          <button 
            onClick={() => setEmailJobSuccessMessage('')}
            className="text-green-500 hover:text-green-700 ml-4"
          >
            ✕
          </button>
        </div>
      )}
      {emailJobErrorMessage && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded flex justify-between items-center">
          <span>{emailJobErrorMessage}</span>
          <button 
            onClick={() => setEmailJobErrorMessage('')}
            className="text-red-500 hover:text-red-700 ml-4"
          >
            ✕
          </button>
        </div>
      )}
      {pollingMessage && isPolling && (
        <div className="mb-4 p-3 bg-blue-100 text-blue-700 border border-blue-300 rounded flex items-center">
          <FaSync className="animate-spin mr-2" />
          {pollingMessage}
        </div>
      )}
      {emailJobStatus && !isPolling && emailJobStatus.details && emailJobStatus.details.length > 0 && (
        <div className="mb-4 p-4 bg-gray-50 border border-gray-300 rounded">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-medium text-gray-700">Email Job Details:</h4>
            <button 
              onClick={() => setEmailJobStatus(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <div className="text-xs text-gray-600 space-y-1 max-h-32 overflow-y-auto">
            {emailJobStatus.details.map((detail, index) => (
              <div key={index} className={`flex justify-between ${
                detail.status === 'success' ? 'text-green-700' : 'text-red-700'
              }`}>
                <span>RCTI #{detail.rcti_number || detail.rcti_id}</span>
                <span>{detail.status === 'success' ? '✓ Sent' : '✗ Failed'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters Toggle */}
      <div className="flex justify-end mb-2">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-800 transition duration-300"
        >
          <FaFilter className="mr-2" size={12} />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
          {showFilters ? <FaChevronUp className="ml-2" size={10} /> : <FaChevronDown className="ml-2" size={10} />}
        </button>
      </div>

      {/* Filters Row */}
      {showFilters && (
        <div className="mb-4">
          {/* Row 1: Searchable filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* Franchisee Filter */}
          <div className="relative">
            <InputField id="fseFilter" label="Filter by Franchisee"
              labelClassName="!text-xs !text-gray-600"
              autoComplete="off"
              value={fseDisplayValue}
              onChange={(e) => handleFseInputChange(e.target.value)}
              onBlur={() => setTimeout(() => franchiseeSearch.setShowSuggestions(false), 150)}
              onFocus={() => franchiseeSearch.suggestions.length > 0 && franchiseeSearch.setShowSuggestions(true)}
              placeholder="Type to search..."
            />
            {franchiseeSearch.showSuggestions && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {franchiseeSearch.isSearching ? (
                  <li className="px-3 py-2 text-gray-500 text-sm">Searching...</li>
                ) : franchiseeSearch.suggestions.length > 0 ? (
                  franchiseeSearch.suggestions.map(fse => (
                    <li
                      key={fse.id}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      onMouseDown={() => handleFseSelect(fse)}
                    >
                      <div className="font-medium">{fse.firstName} {fse.lastName}</div>
                      <div className="text-xs text-gray-500">{fse.franchiseName || fse.fseCode}</div>
                    </li>
                  ))
                ) : fseDisplayValue.length >= 3 && (
                  <li className="px-3 py-2 text-gray-500 text-sm">No franchisees found.</li>
                )}
              </ul>
            )}
          </div>

          {/* Household (Client) Filter */}
          <div className="relative">
            <InputField id="clientFilter" label="Filter by Client"
              labelClassName="!text-xs !text-gray-600"
              autoComplete="off"
              value={householdDisplayValue}
              onChange={(e) => handleHouseholdInputChange(e.target.value)}
              onBlur={() => setTimeout(() => householdSearch.setShowSuggestions(false), 150)}
              onFocus={() => householdSearch.suggestions.length > 0 && householdSearch.setShowSuggestions(true)}
              placeholder="Type to search..."
            />
            {householdSearch.showSuggestions && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {householdSearch.isSearching ? (
                  <li className="px-3 py-2 text-gray-500 text-sm">Searching...</li>
                ) : householdSearch.suggestions.length > 0 ? (
                  householdSearch.suggestions.map(household => {
                    const label = formatHouseholdLabel(household);
                    return (
                      <li
                        key={household.id}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onMouseDown={() => handleHouseholdSelect(household)}
                      >
                        <div className="font-medium">{label}</div>
                        {household.email && <div className="text-xs text-gray-500">{household.email}</div>}
                      </li>
                    );
                  })
                ) : householdDisplayValue.length >= 3 && (
                  <li className="px-3 py-2 text-gray-500 text-sm">No clients found.</li>
                )}
              </ul>
            )}
          </div>

          {/* RCTI Number Filter */}
          <div>
            <InputField id="rctiNumberFilter" label="RCTI Number"
              labelClassName="!text-xs !text-gray-600"
              value={filterRctiNumber || ''}
              onChange={(e) => onFilterChange('rctiNumber', e.target.value || null)}
              onKeyDown={(e) => e.key === 'Enter' && onFilterChange('apply')}
              placeholder="e.g. RCTI-1234"
            />
          </div>
        </div>

        {/* Row 2: Selectable filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">

          {/* Loope Status Filter */}
          <div>
            <SelectField id="statusFilter" label="Loope Status"
              labelClassName="!text-xs !text-gray-600"
              value={selectedStatus}
              onChange={onStatusChange}
            >
              {STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectField>
          </div>

          {/* Xero Status Filter */}
          <div>
            <SelectField id="xeroStatusFilter" label="Xero Status"
              labelClassName="!text-xs !text-gray-600"
              value={filterXeroStatus || ''}
              onChange={(e) => onFilterChange('xeroStatus', e.target.value || null, { autoApply: true })}
            >
              {XERO_STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectField>
          </div>

          {/* Sort by Date */}
          <div>
            <SelectField id="sortDirection" label="Sort by Date"
              labelClassName="!text-xs !text-gray-600"
              value={sortDirection || 'desc'}
              onChange={(e) => onSortDirectionChange(e.target.value)}
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </SelectField>
          </div>
        </div>

        {/* Filter Action Buttons */}
        <div className="flex justify-end space-x-2 mt-3">
          <button
            onClick={handleClearFiltersLocal}
            className="px-3 py-1.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 inline-flex items-center transition duration-300 disabled:opacity-50"
            disabled={loading}
          >
            <FaTimes className="mr-1" size={12} /> Clear
          </button>
          <button
            onClick={handleApplyFilters}
            className="px-3 py-1.5 text-sm font-semibold text-white bg-green-600 hover:bg-green-500 rounded-full shadow-sm inline-flex items-center transition duration-300 disabled:opacity-50"
            disabled={loading || franchiseeSearch.isSearching || householdSearch.isSearching}
          >
            <FaFilter className="mr-1" size={12} /> Apply
          </button>
          <button
            onClick={onCheckDuplicates}
            disabled={checkingDuplicates || loading}
            className={`px-3 py-1.5 text-sm font-semibold rounded-full shadow-sm inline-flex items-center ${
              checkingDuplicates || loading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-yellow-600 text-white hover:bg-yellow-500'
            } transition duration-300`}
          >
            {checkingDuplicates ? (
              <>
                <FaSync className="animate-spin mr-1" size={12} />
                Checking...
              </>
            ) : (
              <>
                <FaExclamationCircle className="mr-1" size={12} />
                Check for Duplicates
              </>
            )}
          </button>
        </div>

        {/* Duplicate Notification Banner */}
        {duplicatesCount > 0 && (
          <div className="mt-3 p-3 bg-red-50 border border-red-300 rounded-md cursor-pointer hover:bg-red-100 transition-colors" onClick={onViewDuplicates}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FaExclamationCircle className="text-red-600 mr-2" size={16} />
                <span className="text-sm font-medium text-red-800">
                  Duplicate payments detected. Click here to view the list ({duplicatesCount})
                </span>
              </div>
            </div>
          </div>
        )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="mb-6 flex items-center justify-start space-x-3 flex-wrap">
        {(() => {
          // SHARED LOGIC: Check if user selected both paid and unpaid invoices
          // This applies to both "Send to Xero" and "Generate ABA" buttons
          const allPaidInvoiceRCTIs = rctis.filter(rcti =>
            rcti.relatedInvoice && rcti.relatedInvoice.status === 'paid'
          );
          const selectedWithPaidInvoice = Array.from(selectedRCTIs).filter(id =>
            allPaidInvoiceRCTIs.some(rcti => rcti.id === id)
          );
          const selectedWithoutPaidInvoice = Array.from(selectedRCTIs).filter(id =>
            !allPaidInvoiceRCTIs.some(rcti => rcti.id === id)
          );
          const hasMixedSelection = selectedWithPaidInvoice.length > 0 && selectedWithoutPaidInvoice.length > 0;

          // ABA-SPECIFIC: For ABA generation, also filter out RCTIs already included in previous ABA files
          const eligibleForABA = allPaidInvoiceRCTIs.filter(rcti => rcti.abaIncluded !== true);
          const selectedEligibleForABA = selectedWithPaidInvoice.filter(id =>
            eligibleForABA.some(rcti => rcti.id === id)
          );

          return (
            <React.Fragment>
              {draftCount > 0 && (
                <React.Fragment>
                  <button
                    onClick={onSelectAllDraft}
                    disabled={selectableDraftsCount === 0}
                    className={`px-4 py-2 text-sm font-semibold rounded-full shadow-sm ${
                      selectableDraftsCount === 0
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : allDraftSelected
                        ? 'bg-red-600 text-white hover:bg-red-500'
                        : 'bg-green-600 text-white hover:bg-green-500'
                    } transition duration-300`}
                  >
                    {allDraftSelected ? 'Deselect All Draft' : `Select All Draft (${selectableDraftsCount})`}
                  </button>

                  {!isDemoMode() && (
                  <button
                    onClick={onSendToXero}
                    disabled={isSendToXeroDisabled || sendingToXero}
                    className={`px-4 py-2 text-sm font-semibold rounded-full shadow-sm inline-flex items-center ${
                      isSendToXeroDisabled || sendingToXero
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-500'
                    } transition duration-300`}
                  >
                    {sendingToXero ? (
                      <React.Fragment>
                        <FaSync className="animate-spin mr-2" />
                        Sending...
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        <FaPaperPlane className="mr-2" />
                        Send to Xero ({selectedRCTIs.size})
                      </React.Fragment>
                    )}
                  </button>
                  )}

                  {/* Button to send selected RCTIs via Email */}
                  <button
                    onClick={handleSendSelectedEmails}
                    disabled={selectedRCTIs.size === 0 || isSendingEmails || sendingToXero || isPolling}
                    className={`px-4 py-2 text-sm font-semibold rounded-full shadow-sm inline-flex items-center ${
                      selectedRCTIs.size === 0 || isSendingEmails || sendingToXero || isPolling
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-500'
                    } transition duration-300`}
                  >
                    {isSendingEmails ? (
                      <React.Fragment>
                        <FaSync className="animate-spin mr-2" />
                        Initiating Emails...
                      </React.Fragment>
                    ) : isPolling ? (
                      <React.Fragment>
                        <FaSync className="animate-spin mr-2" />
                        Processing Emails...
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        <FaEnvelope className="mr-2" />
                        Email Suppliers ({selectedRCTIs.size})
                      </React.Fragment>
                    )}
                  </button>
                </React.Fragment>
              )}

              {/* Generate ABA Button */}
              {!isDemoMode() && (
              <button
                onClick={handleGenerateABA}
                disabled={isGeneratingABA || eligibleForABA.length === 0 || hasMixedSelection}
                className={`px-4 py-2 text-sm font-semibold rounded-full shadow-sm inline-flex items-center mb-2 md:mb-0 ${
                  isGeneratingABA || eligibleForABA.length === 0 || hasMixedSelection
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-500'
                } transition duration-300`}
                title={
                  hasMixedSelection
                    ? 'Cannot generate ABA with mixed paid/unpaid invoices selected'
                    : eligibleForABA.length === 0
                    ? 'No eligible RCTIs (must have paid invoices and not already be in an ABA file)'
                    : ''
                }
              >
                {isGeneratingABA ? (
                  <React.Fragment>
                    <FaSync className="animate-spin mr-2" />
                    Generating ABA...
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <FaFileDownload className="mr-2" />
                    {hasMixedSelection
                      ? 'Generate ABA (Mixed Selection)'
                      : selectedEligibleForABA.length > 0
                      ? `Generate ABA (${selectedEligibleForABA.length})`
                      : eligibleForABA.length > 0
                      ? 'Generate ABA (All)'
                      : 'Generate ABA'}
                  </React.Fragment>
                )}
              </button>
              )}
            </React.Fragment>
          );
        })()}

        {!isDemoMode() && (
        <button
          onClick={onSync}
          disabled={isSyncing}
          className={`px-4 py-2 text-sm font-semibold rounded-full shadow-sm inline-flex items-center ${
            isSyncing
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-500'
          } transition duration-300`}
        >
          {isSyncing ? (
            <React.Fragment>
              <FaSync className="animate-spin mr-2" />
              Syncing...
            </React.Fragment>
          ) : (
            <React.Fragment>
              <FaSync className="mr-2" />
              Sync with Xero
            </React.Fragment>
          )}
        </button>
        )}
      </div>
      <div className="bg-white shadow overflow-hidden rounded-lg">
        {loading ? (
          <p className="text-center text-gray-500 py-10">Loading RCTIs...</p>
        ) : rctis.length === 0 ? (
          <div className="text-center text-gray-500 p-6">
            <p>No RCTIs found{selectedStatus ? ` with status "${selectedStatus.replace('_', ' ')}"` : ''}.</p>
          </div>
        ) : (
          <table className="min-w-full leading-normal">
            <thead>
              <tr className="bg-gray-200 text-left text-gray-600 uppercase text-sm">
                <th className="px-2 py-3 border-b-2 border-gray-200 w-10 text-center">
                  <input
                    type="checkbox"
                    checked={allDraftSelected}
                    onChange={onSelectAllDraft}
                    title={allDraftSelected ? 'Deselect all' : 'Select all'}
                    className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300 rounded cursor-pointer"
                  />
                </th>
                <th className="px-3 py-3 border-b-2 border-gray-200 w-36">RCTI #</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Franchisee</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Client</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Completion Date</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Amount Due</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Loope Status</th>
                <th className="px-4 py-3 border-b-2 border-gray-200 w-28">RCTI Xero</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Invoice Status</th>
              </tr>
            </thead>
            <tbody>
              {rctis.map((rcti) => {
                // Show checkbox for any RCTI that has not yet been sent to Xero successfully.
                // This allows users to send to Xero regardless of invoice status.
                const showCheckbox = rcti.xero_submission_status !== 'success';

                return (
                  <tr key={rcti.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onRowClick(rcti.id)}>
                    <td className="px-2 py-4 border-b border-gray-200 bg-white text-sm text-center" onClick={(e) => e.stopPropagation()}>
                      {showCheckbox ? (
                        <input
                          type="checkbox"
                          checked={selectedRCTIs.has(rcti.id)}
                          onChange={() => onSelectRCTI(rcti.id, rcti.status)}
                          className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300 rounded"
                        />
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap font-medium">{rcti.RCTINumber || rcti.InvoiceNumber}</p>
                      <p className="text-gray-500 whitespace-no-wrap text-xs">{formatDate(rcti.createdAt)}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{(rcti.SupplierName || rcti.ContactName || 'N/A')}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{(rcti.clientName || 'N/A')}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm" onClick={(e) => e.stopPropagation()}>
                      <div>
                        <p className="text-gray-900 whitespace-no-wrap">
                          {rcti.completionDate ? formatDate(rcti.completionDate) : <span className="text-gray-400">-</span>}
                        </p>
                        {rcti.jobId && (
                          <button
                            type="button"
                            className="text-xs text-jims-cyan hover:text-jims-cyan-dark hover:underline cursor-pointer"
                            onClick={() => onCompletionDateClick && onCompletionDateClick(rcti.jobId)}
                            title="View job details"
                          >
                            View job
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap font-medium">{calculateAmountDue(rcti)}</p>
                    </td>
                  <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm" onClick={(e) => e.stopPropagation()}>
                    <div>
                      <StatusPill
                        label={
                          rcti.status === 'awaiting_payment' ? 'Awaiting payment' :
                          rcti.status === 'notified' ? 'FSE Notified' :
                          rcti.status ? rcti.status.charAt(0).toUpperCase() + rcti.status.slice(1).replace('_', ' ') : 'N/A'
                        }
                        pillClass={
                          rcti.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                          rcti.status === 'notified' ? 'bg-blue-100 text-blue-800' :
                          rcti.status === 'paid' ? 'bg-green-100 text-green-800' :
                          rcti.status === 'awaiting_payment' ? 'bg-orange-100 text-orange-600' :
                          rcti.status === 'void' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }
                      />
                      {/* Duplicate Indicator */}
                      {rcti.isDuplicate && (
                        <div className="mt-1">
                          <button
                            type="button"
                            onClick={onViewDuplicates}
                            className="text-xs text-red-600 hover:text-red-800 hover:underline font-semibold cursor-pointer flex items-center"
                          >
                            <FaExclamationCircle className="mr-1" size={10} />
                            <span>Duplicate</span>
                          </button>
                        </div>
                      )}
                      {/* Void action moved to Edit Job page */}
                    </div>
                  </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm" onClick={(e) => e.stopPropagation()}>
                      {rcti.xero_submission_status ? (
                        <StatusPill
                          label={rcti.xero_submission_status.charAt(0).toUpperCase() + rcti.xero_submission_status.slice(1)}
                          pillClass={
                            rcti.xero_submission_status === 'success' ? 'bg-green-100 text-green-800' :
                            rcti.xero_submission_status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }
                          subLabel={
                            rcti.xero_submission_status === 'success' && rcti.xero_invoice_status
                              ? rcti.xero_invoice_status.charAt(0).toUpperCase() + rcti.xero_invoice_status.slice(1).toLowerCase()
                              : undefined
                          }
                          showError={rcti.xero_submission_status === 'failed'}
                          onClick={() => onXeroStatusClick(rcti)}
                        />
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                               <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm" onClick={(e) => e.stopPropagation()}>
                      <div>
                        {rcti.relatedInvoice ? (
                          (() => {
                            const { pillClass, label } = getStatusPillProps(rcti.relatedInvoice.status);
                            const displayLabel = rcti.relatedInvoice.status === 'paid' && rcti.abaIncluded === true 
                              ? `${label} (ABA)` 
                              : label;
                            const content = <StatusPill label={displayLabel} pillClass={pillClass} />;
                            return (
                              <button
                                type="button"
                                className="cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`/invoices/${rcti.relatedInvoice.id}`, '_blank');
                                }}
                                title={`Invoice #${rcti.relatedInvoice.InvoiceNumber}${rcti.abaIncluded ? ' - Already included in ABA file' : ''}`}
                              >
                                {content}
                              </button>
                            );
                          })()
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                  </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        
        {/* Pagination Controls */}
        {!loading && rctis.length > 0 && (
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
              {/* Left side - Page size selector */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Items per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan text-sm"
                >
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={40}>40</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-700">
                  {rctis.length < pageSize ? `Showing ${rctis.length}` : `Showing ${pageSize}`}
                </span>
              </div>

              {/* Right side - Page navigation */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 1 || isNavigating}
                  className={`px-3 py-1 text-sm font-medium rounded-md flex items-center ${
                    currentPage === 1 || isNavigating
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  {isNavigating && currentPage > 1 && <FaSync className="animate-spin mr-1" size={12} />}
                  Previous
                </button>
                
                <span className="text-sm text-gray-700 px-2">
                  Page {currentPage}
                </span>
                
                <button
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={!hasNextPage || isNavigating}
                  className={`px-3 py-1 text-sm font-medium rounded-md flex items-center ${
                    !hasNextPage || isNavigating
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  {isNavigating && hasNextPage && <FaSync className="animate-spin mr-1" size={12} />}
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RCTITabContent;
